'use client'

/**
 * /wettbewerbsordnung — FREE GLIDE LEAGUE Wettbewerbsordnung
 *
 * Komplette Neufassung 2026-05-08:
 *  - Basis: Offizielle FREE GLIDE LEAGUE Regel v1.0.6 (gueltig seit 01.01.2024 / 01.01.2025)
 *    Quelle: https://www.rc-electronics.eu/FreeGlideLeagueInfo.pdf
 *    Autor: Stephan Eich / Modellpilot.EU (Veranstalter)
 *  - GPS-Triangle Klassen entfernt (PDF kennt nur Free Glide)
 *  - Drei-Finger-Regel als eigene §§ verankert
 *  - 750m AGL Wertungshoehe + 5km Mindestdistanz + ENL-Logik aus PDF
 *  - § 19 Verhaltensregeln + Sanktionen + sofortige Sperre
 *  - § 20 Datenschutz mit Bewegungsdaten-Einwilligung
 *  - Uebergangsregel 2026: 7-Tage-Frist erst ab 2027 (FGL-Umzug)
 *  - BGH-konforme Aenderungsklausel (XI ZR 26/20) in § 21
 */

import Link from 'next/link'
import { useTranslation } from '@/i18n/client'
import {
  AwardLegalShell,
  AwardPflichtList,
  type LegalArticle,
} from '@/components/award/legal-shell'
import { AwardGallery } from '@/components/award/award-gallery'
import { WettbewerbGallery } from '@/components/wettbewerb/wettbewerb-gallery'

const SEASON = new Date().getFullYear()

const articles: LegalArticle[] = [
  // ─────────────────────────────────────────────────────────────
  // I. CHARAKTER & VERANSTALTERROLLE
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 0', numDisplay: '00', title: 'Verhältnis zu den Nutzungsbedingungen', titleI18nKey: 'legal.wettbewerbTitles.t00',
    body: (
      <p>
        Diese Wettbewerbsordnung ergänzt die{' '}
        <Link href="/nutzungsbedingungen" className="award-link">Allgemeinen Geschäfts- und Nutzungsbedingungen</Link>{' '}
        der Plattform sowie die{' '}
        <Link href="/datenschutz" className="award-link">Datenschutzerklärung</Link>. Im Konflikt zwischen
        dieser Wettbewerbsordnung und den Nutzungsbedingungen gehen die Nutzungsbedingungen vor,
        soweit nicht ausdrücklich etwas anderes bestimmt ist.
      </p>
    ),
  },
  {
    num: '§ 1', numDisplay: '01', title: 'Charakter der Serie — FUN & Förderung', titleI18nKey: 'legal.wettbewerbTitles.t01',
    body: (
      <>
        <p>
          Die FREE GLIDE LEAGUE ist eine internationale, dezentrale und fortlaufende
          Online-Vergleichsserie für Modellsegelflüge.
        </p>
        <p>
          Die Flüge werden nicht als gemeinsame Vor-Ort-Veranstaltung des Veranstalters durchgeführt,
          sondern von den teilnehmenden Pilotinnen und Piloten eigenverantwortlich an selbst gewählten
          Orten und zu selbst gewählten Zeiten — in gewohnter Umgebung, etwa im Verein, auf dem
          Modellflugplatz oder einem Modellfluggelände — über freie Strecken ohne festgelegte Start-
          oder Ziellinien und ohne Einschränkungen beim Modellsegelflugzeug.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Sinn und Fokus der FREE GLIDE LEAGUE</strong>{' '}
          ist die Freude am Modellsegelflug, die Förderung des Modellflugsports, das persönliche
          Erfolgserlebnis jeder Pilotin und jedes Piloten und die Bildung einer engagierten,
          respektvollen Gemeinschaft. Wer als Pilot die Serie nutzt, soll dabei sein eigenes Maximum
          an Freude und persönlichem FGL-Erfolg erreichen — nichts darüber hinaus.
        </p>
        <p>
          <strong>Die Serie ist ausdrücklich kein offizieller Wettbewerb</strong> der
          Fédération Aéronautique Internationale (FAI), keiner nationalen Luftsportorganisation
          (z. B. DAeC, AeCS, AeC) und keines anderen Sportverbandes. Wertungen, Platzierungen und
          Auszeichnungen der FREE GLIDE LEAGUE sind <em>inoffiziell</em>. Sie begründen keine Rekorde
          im Sinne der FAI, des OLC, der WeGlide-Wertung oder vergleichbarer Verbände und werden
          von diesen weder anerkannt noch geprüft. Es handelt sich um inoffizielle
          Streckenflugleistungen — bekannt und akzeptiert.
        </p>
        <p>
          Die Teilnahme an der FREE GLIDE LEAGUE ist und bleibt kostenfrei.
        </p>
      </>
    ),
  },
  {
    num: '§ 2', numDisplay: '02', title: 'Veranstalterrolle', titleI18nKey: 'legal.wettbewerbTitles.t02',
    body: (
      <>
        <p>
          Veranstalter der Serie, der Regeln, der Klassen, der Auswertung, der Ranglisten,
          der Prüfentscheidungen und der Pokalvergabe ist <strong>Stephan Eich, Modellpilot.EU</strong>{' '}
          (Anschrift siehe <Link href="/impressum" className="award-link">Impressum</Link>).
        </p>
        <p>
          Der Veranstalter ist grundsätzlich <strong>nicht</strong> Veranstalter des einzelnen,
          dezentral durch die Pilotin oder den Piloten durchgeführten Fluges. Die Verantwortung für
          den einzelnen Flug verbleibt vollständig bei der Pilotin / dem Piloten.
        </p>
        <p>
          Öffentliche Veranstaltungen von Wettbewerben oder Schauvorstellungen, an denen
          Luftfahrzeuge beteiligt sind, sind rechtlich gesondert als Luftfahrtveranstaltungen zu
          betrachten und bedürfen grundsätzlich einer Genehmigung. Diese Wettbewerbsordnung betrifft
          eine dezentrale Wertungsserie und nicht automatisch eine solche
          Vor-Ort-Luftfahrtveranstaltung.
        </p>
      </>
    ),
  },
  {
    num: '§ 3', numDisplay: '03', title: 'Teilnahmeberechtigung', titleI18nKey: 'legal.wettbewerbTitles.t03',
    body: (
      <>
        <p>
          Teilnahmeberechtigt sind natürliche Personen ab Vollendung des{' '}
          <strong>16. Lebensjahres</strong>, die diese Wettbewerbsordnung sowie die Nutzungsbedingungen
          und die Datenschutzerklärung akzeptieren und einen nach dieser Wettbewerbsordnung wertbaren
          Flug einreichen. Das Mindestalter folgt der DSGVO Art. 8 (eigenständige Einwilligungs­fähigkeit).
        </p>
        <p>
          Der Veranstalter kann ergänzende Voraussetzungen festlegen, insbesondere technische
          Mindestanforderungen an Logger oder Modelle.
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // II. SAISON & FRISTEN
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 4', numDisplay: '04', title: 'Saison, Einreichungsfristen und offizielle Zeitbasis', titleI18nKey: 'legal.wettbewerbTitles.t04',
    body: (
      <>
        <p>
          Die Wertungssaison beginnt jeweils am 01.01. eines Jahres um 00:00 UTC und endet am 31.12.
          desselben Jahres um 23:59:59 UTC.
        </p>
        <p>
          Für die gesamte Serie gilt <strong>UTC als allein maßgebliche offizielle Zeitbasis</strong>.
          Die Wahl von UTC dient der internationalen Vergleichbarkeit und schließt
          Zeitzonen-Streitigkeiten aus.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Saison-Nachreichfrist:</strong> Flüge, die
          innerhalb des Saisonzeitraums tatsächlich durchgeführt wurden, können bis spätestens
          07.01. des Folgejahres um 24:00 UTC eingereicht werden. Nach Ablauf dieser Frist
          eingehende Flüge oder unvollständige Einreichungen werden nicht mehr berücksichtigt,
          es sei denn, der Veranstalter hat die Verspätung selbst zu vertreten.
        </p>
        <p>
          <strong>Übergangsregelung Saison 2026:</strong> Aufgrund des Umzugs der FREE GLIDE LEAGUE
          auf die neue Plattform gilt für die Saison 2026 ausschließlich die Saison-Nachreichfrist
          bis 07.01.2027 24:00 UTC. Eine zusätzliche zeitnahe Einreichungspflicht innerhalb von
          7 Tagen nach Flugdurchführung besteht für die Saison 2026 <em>nicht</em>.
        </p>
        <p>
          <strong>Ab Saison 2027:</strong> Flüge müssen innerhalb von <strong>7 Kalendertagen</strong>{' '}
          nach tatsächlicher Flugdurchführung eingereicht werden. Diese Frist dient der zeitnahen
          Wertungsführung und der Vermeidung nachträglicher Manipulation.
        </p>
        <p>
          Eine Preisbewerbung ist nur mit festgelegter Bewerbungsfrist rechtlich sauber ausgestaltet;
          deshalb sind Saisonende und Nachreichfrist verbindlicher Bestandteil dieser Ordnung.
        </p>
      </>
    ),
  },
  {
    num: '§ 5', numDisplay: '05', title: 'Internationale Geltung', titleI18nKey: 'legal.wettbewerbTitles.t05',
    body: (
      <>
        <p>
          Die Serie ist weltweit allen Pilotinnen und Piloten offen, unabhängig von Nationalität,
          Wohnsitz oder Vereinszugehörigkeit. Eine Bindung an eine bestimmte nationale
          Luftsportorganisation oder an Regelwerke der Fédération Aéronautique Internationale (FAI)
          besteht nicht — siehe auch § 1.
        </p>
        <p>
          Für die sportliche Einordnung, Klassenzuordnung und Serienwertung gelten ausschließlich
          diese Wettbewerbsordnung und die vom Veranstalter veröffentlichten technischen Regeln.
        </p>
        <p>
          Der Datenschutz-Standard der Serie orientiert sich an der DSGVO und damit am weltweit
          höchsten Schutzniveau; Nutzer aus anderen Ländern (z. B. Schweiz, UK, Kanada, Brasilien)
          haben nach ihrem jeweiligen Heimatrecht gegebenenfalls zusätzliche Rechte (siehe{' '}
          <Link href="/datenschutz" className="award-link">Datenschutzerklärung</Link>).
        </p>
        <p>
          Für die Durchführung des konkreten Fluges gelten zusätzlich und vorrangig die zwingenden
          Vorschriften des jeweiligen Flugortes und Staates, in dem gestartet und geflogen wird.
        </p>
      </>
    ),
  },
  {
    num: '§ 6', numDisplay: '06', title: 'Eigenverantwortung der Pilotinnen und Piloten', titleI18nKey: 'legal.wettbewerbTitles.t06',
    body: (
      <>
        <p>
          Jede Pilotin / jeder Pilot fliegt zu <strong>100 % eigenverantwortlich</strong> und kann
          den Veranstalter, die Rennleitung sowie die Ansprechpartnerinnen und Ansprechpartner der
          FREE GLIDE LEAGUE im Schadensfall, bei Verstößen oder in anderen Fällen rechtlich nicht
          zur Verantwortung ziehen. Sie / er stellt diese grundsätzlich von der Haftung frei.
        </p>
        <p>Insbesondere ist jede Pilotin / jeder Pilot allein dafür verantwortlich,</p>
        <AwardPflichtList items={[
          'dass der Flug am gewählten Ort rechtlich zulässig ist,',
          'dass der Flug innerhalb der Sichtgrenze (VLOS — visual line of sight) durchgeführt wird,',
          'dass örtliche Luftraum-, Gelände-, Sicherheits-, Naturschutz- und Vereinsregeln eingehalten werden,',
          'dass eine ausreichende Haftpflichtversicherung am Aufstiegsort besteht,',
          'dass erforderliche Registrierungen, Kennzeichnungen, Berechtigungen und Freigaben (z. B. Kenntnisnachweis) vorliegen,',
          'dass das Modell technisch sicher ist und seine Betriebsgrenzen (MTOM, Lastvielfaches, Geschwindigkeitsbereich, Herstellervorgaben) eingehalten werden,',
          'dass der Flug sicher durchgeführt wird.',
        ]} />
        <p>
          Für Deutschland gilt zusätzlich, dass der Betrieb von Flugmodellen im Rahmen von
          Luftsportverbänden unter den Voraussetzungen des § 21f LuftVO in Verbindung mit
          Art. 16 der Durchführungsverordnung (EU) 2019/947 stattfinden kann; außerhalb Deutschlands
          gelten die jeweiligen nationalen und lokalen Regelungen.
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // III. KLASSEN & GELÄNDEARTEN
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 7', numDisplay: '07', title: 'Wertungsklassen und Zeitwertungen', titleI18nKey: 'legal.wettbewerbTitles.t07',
    body: (
      <>
        <p>Gewertet wird ausschließlich in den folgenden Klassen und Zeitfenstern:</p>
        <div className="award-laender-grid">
          <div className="award-land">
            <div className="award-land-code">CL</div>
            <div>
              <div className="award-land-name">Club-Klasse</div>
              <div className="award-land-text">Spannweite bis 4.000 mm</div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">ST</div>
            <div>
              <div className="award-land-name">Standard-Klasse</div>
              <div className="award-land-text">Spannweite 4.001 mm bis 6.000 mm</div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">OF</div>
            <div>
              <div className="award-land-name">Offene Klasse</div>
              <div className="award-land-text">Spannweite ab 6.001 mm</div>
            </div>
          </div>
          <div className="award-land">
            <div className="award-land-code">ZW</div>
            <div>
              <div className="award-land-name">Zeitwertungen</div>
              <div className="award-land-text">30 min · 1 h · 2 h · 3 h · &gt; 3 h</div>
            </div>
          </div>
        </div>
        <p>
          Mit nur einem Flug können mehrere Zeitwertungen erzielt werden — ein Flug von 2:23 h zählt
          zugleich für die 30-min-, 1-h- und 2-h-Wertung. Voraussetzung jeder Zeitwertung ist eine
          tatsächlich geflogene Streckendistanz von mindestens <strong>5 km</strong>.
        </p>
        <p>
          In jeder Online-Rangliste werden die jeweils besten fünf Ergebnisse einer Pilotin / eines
          Piloten für den aktuellen Rang berücksichtigt. Weitere eingereichte Einzelflüge erweitern
          die jeweiligen Wertungslisten.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Hinweis zu GPS-Triangle:</strong>{' '}
          Flüge, welche GPS-Triangle-IGC-Dateien sind, können analysiert werden und unter
          der FGL-Wertung mit einfließen, wenn die FGL-Bewertungsregeln eingehalten werden.
        </p>
      </>
    ),
  },
  {
    num: '§ 8', numDisplay: '08', title: 'Geländearten — Flachland & Hang', titleI18nKey: 'legal.wettbewerbTitles.t08',
    body: (
      <>
        <p>
          Die Wertungsflüge werden über den <strong>Startpunkt des Modells</strong> automatisch
          dem Gelände zugewiesen. Die Klassifizierung erfolgt auf Basis eines{' '}
          <strong>Digitalen Geländemodells (DGM)</strong> in Verbindung mit Referenzdaten aus
          aktiven Flügen und Admin-Vorgaben — der Flug wird so eindeutig dem Gelände{' '}
          <strong>Flachland</strong> oder <strong>Hang</strong> zugeordnet und in der jeweiligen
          Wertung berücksichtigt.
        </p>
        <p>
          Die Beurteilung erfolgt über drei konzentrische <strong>Bewertungs-Radien</strong> um
          den Startpunkt:
        </p>
        {/* R107.16.2: hardcoded Hex auf .fgl-marker--* umgestellt — Theme-aware Pille
            mit Background-Tint + Border, lesbar in hellen + dunklen Themes */}
        <ul style={{ marginTop: '0.5em', paddingLeft: '1.2em', lineHeight: 1.9 }}>
          <li>
            <strong className="fgl-marker fgl-marker--vlos">VLOS (Visual Line of Sight) — 450 m</strong>{' '}
            <span className="fgl-prose-subtle">(gelber Ring)</span>: unmittelbarer Sichtbereich um
            den Startpunkt.
          </li>
          <li>
            <strong className="fgl-marker fgl-marker--bvlos-t1">BVLOS-T1 — 800 m</strong>{' '}
            <span className="fgl-prose-subtle">(cyan Ring)</span>: erweiterte Bewertungs-Zone.
          </li>
          <li>
            <strong className="fgl-marker fgl-marker--bvlos-t2">BVLOS-T2 — 1500 m</strong>{' '}
            <span className="fgl-prose-subtle">(violetter Ring)</span>: maximaler Bewertungs-Radius.
          </li>
        </ul>
        <p>
          Diese sehr aufwendige, zugleich aber sehr faire Beurteilung des Geländes ist weltweit
          eindeutig und gilt für jeden Startpunkt gleichermaßen — unabhängig von Region oder
          Wettbewerbssaison.
        </p>

        <div style={{ marginTop: '1.4em' }}>
          <h4 style={{
            color: 'var(--award-gold)',
            fontSize: '0.95em',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '0.8em',
          }}>
            Referenzpunkte — Beispiele aus dem aktiven Referenzdaten-Bestand
          </h4>
          <p style={{ fontSize: '0.92em', opacity: 0.85, marginBottom: '1em' }}>
            Die folgenden sechs Beispiele zeigen, wie unterschiedliche Geländeformen automatisch
            erkannt werden — vom reinen Flachland-Acker über Hügel- und Vorgebirgs-Lagen bis zur
            klaren Hang-Position im alpinen Raum:
          </p>
          <WettbewerbGallery
            slots={[
              { slot: 'terrain-01', alt: 'Referenzpunkt 1 — Alpenrand Flachland-Übergang mit allen drei Radien', caption: 'Alpenrand · Übergang Flachland → Vorland' },
              { slot: 'terrain-02', alt: 'Referenzpunkt 2 — Hang im Mittelgebirge mit zentralem Startpunkt-Pin', caption: 'Mittelgebirge · klarer Hang-Referenzpunkt' },
              { slot: 'terrain-03', alt: 'Referenzpunkt 3 — Alpiner Hang über bewaldetem Tal',                   caption: 'Alpiner Hang über Tal' },
              { slot: 'terrain-04', alt: 'Referenzpunkt 4 — Hochlage mit weitem Talblick',                        caption: 'Hochlage mit Talblick' },
              { slot: 'terrain-05', alt: 'Referenzpunkt 5 — Hügeliges Mittelgelände am Stadtrand',                caption: 'Hügeliges Mittelgelände am Siedlungsrand' },
              { slot: 'terrain-06', alt: 'Referenzpunkt 6 — Reines Flachland mit Online-Pin und Referenz-Stern', caption: 'Reines Flachland · Felder-Mosaik' },
            ]}
          />
        </div>

        <p style={{ marginTop: '1.4em' }}>
          Sollte nach Auffassung der Pilotin / des Piloten eine falsche Deklaration des Fluges
          vorliegen, kann diese{' '}
          <strong>über ein Formular auf der Plattform begründet beanstandet</strong> werden. Folgt
          die Administration der Begründung fachlich und sachlich, wird das Gelände entsprechend
          für Flachland oder Hang als Referenz aufgenommen. So entsteht über die Zeit eine{' '}
          <strong>belastbare Matrix von Referenzpunkten</strong>, die zunehmend dichter, fairer
          und sportrechtlich tragfähiger wird.
        </p>

        <p>
          <strong>Definitionen zur Orientierung:</strong>
        </p>
        <p>
          <strong>Flachland (flatland):</strong> Ein Gebiet ist dann Flachland, wenn dort kein
          Hangaufwind genutzt werden kann, um die Höhe zu halten. Leicht hügelige Gelände gelten
          nicht als Hangfluggelände.
        </p>
        <p>
          <strong>Hang (slope):</strong> Ein Hangfluggelände ist ein Gebiet, in dem durch
          orographisch bedingte Aufwinde die Höhe gehalten oder gesteigert werden kann — unabhängig
          davon, ob dies zusätzlich von Thermik unterstützt wird.
        </p>
        <p>
          <strong>Entscheidungshilfe / Protestfall:</strong> Im Zweifelsfall prüft die Pilotin / der
          Pilot eigenverantwortlich anhand des IGC-Files (z. B. KML-Konvertierung in Google Earth
          oder IGC-Viewer-App mit 3D-Geländedarstellung), wie das Gelände einzuordnen ist. Im
          Protestfall trägt die Beweislast der Antragsteller; die Rennleitung wird mediativ tätig
          und entscheidet final im Einzelfall. Die betroffene Pilotin / der betroffene Pilot reicht
          das IGC-File auf Aufforderung innerhalb von 14 Tagen ein, andernfalls behält sich die
          Rennleitung weitere Entscheidungen auch ohne Prüfung vor.
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // IV. DREI-FINGER-REGEL (Start · Wertungsflug · Flugende)
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 9', numDisplay: '09', title: 'Drei-Finger-Regel — Übersicht', titleI18nKey: 'legal.wettbewerbTitles.t09',
    body: (
      <>
        <p>
          Der Wertungsflug folgt der bewusst einfach gehaltenen{' '}
          <strong>Drei-Finger-Regel</strong>: <strong>Start (§ 10) · Wertungsflug (§ 11) · Flugende (§ 12)</strong>.
          Diese drei Phasen sind sowohl rechtlich als auch technisch verbindlich.
        </p>
        <p>
          Maximale Wertungshöhe der gesamten FREE GLIDE LEAGUE ist <strong style={{ color: 'var(--award-gold)' }}>750 m AGL</strong>{' '}
          (2.460,6 ft.) — siehe § 11.
        </p>

        {/* R102.6 (2026-05-31): Drei-Finger-Regel-Infografik — dynamisch aus DB. */}
        <div style={{ marginTop: '1.5em', maxWidth: '760px' }}>
          <WettbewerbGallery
            minColWidth={400}
            aspect="9/16"
            slots={[{
              slot: 'drei-finger-regel',
              alt: 'Drei-Finger-Regel der FREE GLIDE LEAGUE — Start 5–400 m AGL, Wertungsflug max. 750 m und mind. 5 km, Flugende mit 7-Tage-Upload-Frist; plus Übersicht Wettbewerbsklassen Club/Standard/Offen und Flachland vs. Hangflug, 20 personalisierte Pokale',
              caption: 'Drei-Finger-Regel: Start (5–400 m AGL) · Wertungsflug (max. 750 m, min. 5 km, ohne Motor) · Flugende (Landung + Upload innerhalb 7 Tagen). Klick öffnet die Infografik in voller Größe.',
            }]}
          />
        </div>
      </>
    ),
  },
  {
    num: '§ 10', numDisplay: '10', title: 'Start (2.1) — Startfenster, Startarten, Startzeitpunkt', titleI18nKey: 'legal.wettbewerbTitles.t10',
    body: (
      <>
        <p>
          Der Start erfolgt in allen drei Klassen (Club, Standard, Offen) innerhalb eines
          gemeinsamen vertikalen FREE-GLIDE-LEAGUE-Startfensters. Mögliche luftrechtliche
          Beschränkungen am Aufstiegsort sind vorrangig zu beachten.
        </p>
        <p><strong>Zugelassene Startarten vor dem Start:</strong></p>
        <AwardPflichtList items={[
          'Windenstart',
          'Gummiseil',
          'Elektroantrieb (SLS / FES)',
          'Handstart',
          'Huckepack',
          'Seglerschlepp',
        ]} />
        <p>
          <strong>Startfenster:</strong> Während des Startvorgangs dürfen <strong>5 m AGL</strong>{' '}
          (16,4 ft.) nicht unterschritten und <strong>400 m AGL</strong> (1.312 ft.) nicht
          überschritten werden.
        </p>
        <p>
          <strong>Definition des Startzeitpunkts (STA):</strong> Der Start gilt als erfolgt, wenn
          im IGC-File innerhalb dieses vertikalen Startfensters ein permanenter, unterbrechungsfreier
          Höhenverlust (auch sehr minimal) über mindestens <strong>10 Sekunden</strong> bei einem
          ENL <strong>unter 30 %</strong> verzeichnet wird. Die Auswertungssoftware ermittelt diesen
          Zeitpunkt automatisch und stellt ihn im Barogramm einschließlich des ENL-Wertes dar.
          Die nach dem Start erreichten Zeitwertungen werden ebenfalls angezeigt.
        </p>
      </>
    ),
  },
  {
    num: '§ 11', numDisplay: '11', title: 'Wertungsflug (2.2) — Höhe, Motor, Multistart', titleI18nKey: 'legal.wettbewerbTitles.t11',
    body: (
      <>
        <p>
          Der Wertungsflug erfolgt nach erfolgreichem Startvorgang im Gleitflug bis zum Flugende
          (§ 12). Der Motor sollte ausschließlich zum Höhengewinn vor dem Startvorgang genutzt werden.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Maximale Wertungshöhe — 750 m AGL:</strong>{' '}
          Auch wenn die Obergrenze des Luftraum G (Golf) am Aufstiegsort darüber liegt, dürfen
          750 m AGL (2.460,6 ft.) im Wertungsflug nicht überschritten werden. Ein Wertungsflug endet
          bei Überschreitung ab 750,01 m AGL und wird nicht gewertet. <em>Eine Toleranz ist
          ausgeschlossen, selbst wenn die Überschreitung nur eine Sekunde andauert und nur einen
          Zentimeter beträgt.</em>
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>
            Erkennung „Motor an" — analog zur Terrainerkennung über ein
            Detektor-Labor:
          </strong>{' '}
          Die FREE GLIDE LEAGUE erkennt einen laufenden Motor nicht über eine
          einzelne starre Schwelle, sondern über ein <strong>mehrstufiges
          Detektor-Labor</strong> — vergleichbar mit der Geländeerkennung (§ 8).
          Dabei werden ENL (Engine Noise Level) und weitere Sensorwerte (Vario,
          Geschwindigkeit, Höhenverlauf, Beschleunigung, Lage- und Spektral-Signaturen)
          intelligent kombiniert. So lassen sich <strong>echte Motorgeräusche
          klar von harmlosen Fluggeräuschen</strong> — etwa eines schnellen
          Überflugs im Speed-Modus oder eines knackigen Hochziehens während des
          „Power-Fun"-Manövers — unterscheiden, ohne dass die Wertung leidet.
        </p>
        <p>
          <strong>Wie der Admin das Detektor-Labor pflegt:</strong> Im internen
          Lab wird ein Flug geladen, der relevante Bereich am IGC-Track markiert,
          die Logik „Regel ableiten" angewandt, ggf. nachjustiert (tweaken) und
          gespeichert. Anschließend wird die neue Regel <strong>live auf alle
          Flüge des aktuellen Wertungsjahres</strong> angewandt. Jede Anwendung
          wird als <strong>versionierter Snapshot</strong> archiviert; bereits
          abgeschlossene Wertungsjahre lassen sich nur mit einer Pflicht-Bestätigung
          erneut anpassen, um historische Ergebnisse stabil zu halten.
        </p>
        <p>
          <strong>Was du als Pilotin / Pilot tun kannst:</strong> Wer eine
          Motor-Erkennung an einem eigenen Flug für unzutreffend hält, kann den
          Flug auf der Plattform mit einer kurzen Begründung einreichen. Folgt
          die Administration der Begründung fachlich und sachlich, fließt der
          Flug als neuer Referenzfall in die Detektor-Matrix ein — diese wird
          so von Saison zu Saison <strong>genauer, fairer und belastbarer</strong>.
        </p>

        {/* R102.6 (2026-05-31): Detektor-Labor-Sidebar-Screenshot — dynamisch aus DB. */}
        <div style={{ marginTop: '1.4em', maxWidth: '460px' }}>
          <WettbewerbGallery
            minColWidth={320}
            aspect="3/4"
            slots={[{
              slot: 'detektor-labor',
              alt: 'Admin-Sidebar mit dem Bereich „Engine & Klassifikation": Regeln, Parameter, Detektor-Labor (aktiv), Polaren, Polar-Anomalien, ENL-Anomalien, Terrain-Verifikation',
              caption: 'Detektor-Labor — Teil des internen Admin-Workflows „Engine & Klassifikation". Klick öffnet die Vollansicht.',
            }]}
          />
        </div>
        <p>
          <strong>Noisy Flight:</strong> Undichte Kabinenhauben, Logger zu nah an lauten Servos,
          undichte Anschlüsse oder Öffnungen in Rumpf und Tragflächen können das ENL dauerhaft über
          30 % bringen. Dann lässt sich nicht unterscheiden, ob der Motor lief; der Flug wird
          ungültig. Eine Dämpfung des Loggers ist nicht zulässig und wird automatisch erkannt.
          Abhilfe schaffen veränderte Logger-Position im Rumpf oder das Abkleben von Spalten.
        </p>
        <p>
          <strong>Motorstart nach dem Startvorgang</strong> ist nur für Notsituationen gedacht. Wird
          der Motor erneut gestartet und sind weniger als 30 Minuten reine Segelflugzeit erreicht,
          wird der Flug nicht gewertet. Wurde im reinen Segelflug bereits eine Zeitwertung erreicht
          und anschließend der Motor eingeschaltet, fällt die Wertung auf die zuletzt ohne Motor
          erreichte Zeit zurück.
        </p>
        <p>
          <strong>Multistart sind in der FREE GLIDE LEAGUE nicht erlaubt.</strong> Wird im
          Wertungsflug der Motor erneut gestartet und dabei ein erneuter Start (5 m – 400 m AGL,
          10 s Sinken, ENL &lt; 30 %) ermittelt, wird der Flug insgesamt nicht gewertet.
        </p>
      </>
    ),
  },
  {
    num: '§ 12', numDisplay: '12', title: 'Flugende (2.3) — Landung & Einreichungsfrist', titleI18nKey: 'legal.wettbewerbTitles.t12',
    body: (
      <>
        <p>
          Der erfolgreiche Wertungsflug (mindestens 5 km Streckendistanz) endet mit der Landung.
          Die Landung markiert den Beginn der Einreichungsfrist.
        </p>
        <p>
          <strong>Einreichungsfrist Saison 2026:</strong> ausschließlich Saison-Nachreichfrist bis
          07.01.2027 24:00 UTC (siehe § 4 — Übergangsregel).
        </p>
        <p>
          <strong>Einreichungsfrist ab Saison 2027:</strong> innerhalb von <strong>7 Kalendertagen</strong>{' '}
          nach Landung.
        </p>
        <p>
          Überschreitet ein Flug die zulässige Wertungshöhe von 750 m AGL, wird er automatisch nicht
          gewertet. Der Flug bleibt im persönlichen Logbuch der Pilotin / des Piloten zur eigenen
          Analyse einsehbar.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Wertungsende durch Motor-Erkennung:</strong>{' '}
          Das Wertungsende kann auch automatisch ausgelöst werden, wenn das Detektor-Labor (§ 11)
          während des Fluges einen Motor-Einsatz erkennt. In diesem Fall wird der Flug nicht
          insgesamt verworfen, sondern auf die <strong>zuletzt im reinen Segelflug erreichte
          Zeitwertung mit der dabei erreichten Streckendistanz</strong> zurückgewertet — die
          Pilotin / der Pilot behält also alle Punkte, die bis zum Motor-Einsatz sauber erflogen
          wurden.
        </p>
        <p style={{ marginTop: '0.5em', padding: '0.85rem 1rem', borderLeft: '3px solid var(--award-gold)', background: 'color-mix(in oklch, var(--award-gold) 8%, transparent)' }}>
          <strong style={{ color: 'var(--award-gold)' }}>Beispiel:</strong>{' '}
          Eine Pilotin erfliegt nach 1 h sauberen Segelflug nach dem FGL-Start eine
          Wertungsstrecke von 27,4 km — damit ist die Zeitwertung „1 Stunde" mit 27,4 km
          gesetzt. Nach 1 h 38 min hat sie schon 34 km erreicht und sie schaltet kurz den
          Motor ein (z. B. aus Notsituation oder versehentlich). Das Detektor-Labor erkennt
          den Motor-Einsatz und beendet die Wertung an diesem Punkt automatisch. Der Flug
          wird mit <strong>1 Stunde · 27,4 km</strong> gewertet — die erreichten Zeitwertungen
          im reinen Segelflug vor dem Motor-Einsatz bleiben vollständig erhalten. Höhere
          Zeitwertungen (2 h / 3 h) sind dann jedoch nicht mehr erreichbar, da der reine
          Wertungsflug an dieser Stelle endet. Für den Neustart einer Wertung muss gelandet
          und wieder neu gestartet werden. (Tipp: Logger aus und dann wieder einschalten
          erzeugt einen neuen IGC-File für die Auswertung.)
        </p>
        <p>
          <strong>Praxishinweis:</strong> Nach der Landung muss der Logger durch Aus- und
          Wiedereinschalten neu aktiviert werden, bevor er für den nächsten Wertungsflug bereit ist.
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // V. LOGGER & EINREICHUNG
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 13', numDisplay: '13', title: 'IGC-Logger & IGC-Files', titleI18nKey: 'legal.wettbewerbTitles.t13',
    body: (
      <>
        <p>
          Alle Wertungsflüge werden im IGC-Standard aufgezeichnet und auf der Plattform
          hochgeladen.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Empfohlen — Logger mit
          aktivem Manipulationsschutz:</strong> Die folgenden Logger zeichnen den Flug so auf,
          dass die <strong>integrierten Manipulationsschutz-Merkmale</strong> (G-Record,
          Signaturen, geschützte Header-Felder) im IGC-File maschinell erkennbar sind.
          Solche Files werden bei der automatischen Wertung bevorzugt behandelt, weil sie
          ohne weitere Prüfung als unverändert gelten:
        </p>
        <AwardPflichtList items={[
          'Alle Logger und die Albatross-App von RC Electronics',
          'GPS-Logger 2 und GPS-Logger 3 von SM-Modellbau',
        ]} />
        <p>
          <strong>Andere IGC-Files:</strong> Es können auch IGC-Dateien <em>anderer</em>{' '}
          Hersteller eingereicht werden, sofern sie <strong>strikt dem IGC-Standard
          entsprechen</strong>. Fehlt der Manipulationsschutz oder ist die Datei in
          relevanten Punkten nicht standardkonform, wird die Wertung über das{' '}
          <strong>automatische Erkennungssystem</strong> der Plattform geprüft und der Flug
          ggf. vom Administrator manuell freigegeben oder zurückgewiesen.
        </p>
        <p>
          Die Liste der Logger mit aktivem Manipulationsschutz wird vom Veranstalter laufend
          ergänzt und auf der Plattform veröffentlicht. Manipulierte Hardware oder gezielte
          Verfälschung der IGC-Files führt zum Ausschluss gemäß § 18.
        </p>
      </>
    ),
  },
  {
    num: '§ 14', numDisplay: '14', title: 'Einreichung der Streckenflüge & Wertungslogik', titleI18nKey: 'legal.wettbewerbTitles.t14',
    body: (
      <>
        <p>
          Die IGC-Dateien werden auf der FGL-Plattform nach kostenfreier Registrierung im
          persönlichen Logbuch der Pilotin / des Piloten hochgeladen.
        </p>
        <p>
          Ab dem von der Auswertungssoftware festgestellten regelkonformen Startzeitpunkt werden
          bis zur Landung im Startbereich <strong>alle geloggten Meter</strong> gewertet — unabhängig
          davon, ob gekreist oder Strecke geflogen wird (sogenanntes <em>cat&apos;s cradle</em>).
        </p>
        <p>
          <strong>Einwegstrecken von A nach B werden nicht gewertet</strong> — nur Flüge, die zum
          Startbereich zurückkehren bzw. in dessen Nähe enden, sind valide.
        </p>
        <p>
          Die geflogene Distanz erscheint anschließend als SCORE (z. B. „SCORE 76,3 km").
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // VI. WERTUNG & POKALE
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 15', numDisplay: '15', title: 'Wertung & Auswertung', titleI18nKey: 'legal.wettbewerbTitles.t15',
    body: (
      <>
        <p>
          Die sportliche Auswertung erfolgt ausschließlich nach diesen Regeln und der auf der
          Plattform implementierten Prüf- und Auswertungslogik.
        </p>
        <p>
          Der Veranstalter entscheidet über Gültigkeit, Ungültigkeit, Klassen- und
          Geländezuordnung sowie Rangfolge der eingereichten Flüge. Wenn keine gesonderte Jury
          benannt ist, trifft der Veranstalter die Entscheidung selbst — entsprechend dem
          gesetzlichen Grundmodell des § 661 BGB für Preisausschreiben.
        </p>
        <p>
          <strong>Proteste</strong> gegen veröffentlichte Wertungen werden ausschließlich über
          die <strong>internen Protestformulare auf der Plattform</strong> eingereicht — direkt
          aus dem persönlichen Logbuch heraus für{' '}
          <strong>Beanstandung der eigenen Wertung</strong>, bzw. aus der öffentlichen
          Flugansicht über die Schaltfläche „Protest einlegen" für{' '}
          <strong>Protest gegen die Wertung einer anderen Pilotin / eines anderen Piloten</strong>.
          Eine Übersicht aller eigenen Anliegen liegt unter{' '}
          <Link href="/app/protests" className="award-link">„Meine Proteste"</Link>.
        </p>
        <p>
          Der Protest muss innerhalb von <strong>7 Kalendertagen nach Veröffentlichung</strong>{' '}
          der Wertung auf der Plattform eingereicht werden. Die Frist beginnt mit dem
          Veröffentlichungszeitpunkt; maßgeblich ist der Zeitstempel des Plattform-Servers in
          UTC. Pflichtangaben sind: der beanstandete Flug (Auswahl im Formular), die
          beanstandete Entscheidung sowie eine nachvollziehbare Begründung. Eine E-Mail-Einreichung
          ist nicht mehr vorgesehen, damit jede Eingabe vollständig, eindeutig zuordenbar und
          rechtssicher protokolliert ist.
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Protestnote (nur bei Fremd-Protesten):</strong>{' '}
          Mit jedem Protest <em>gegen die Wertung einer anderen Pilotin / eines anderen Piloten</em>{' '}
          ist eine <strong>Protestnote in Höhe von 250 €</strong> per PayPal an{' '}
          <a href="mailto:info@modellpilot.eu" className="award-link">info@modellpilot.eu</a>{' '}
          zu entrichten. Die Plattform zeigt im Protest-Formular die PayPal-Zahlungsdaten an
          und verknüpft die eingehende Zahlung anschließend mit dem eingereichten Protest. Die
          Protestnote dient ausschließlich der Deckung des durchschnittlichen Verwaltungs- und
          Prüfaufwands (Sichtung der IGC-Datei, Re-Run der Auswertungssoftware, Klassen- bzw.
          Geländezuordnungs-Prüfung, schriftliche Stellungnahme).
        </p>
        <p>
          <strong className="fgl-marker fgl-marker--success">Übergangsregelung Saison 2026 — eigene Wertungsanfechtungen bleiben immer kostenfrei:</strong>{' '}
          Da die Plattform am 03.06.2026 neu live geht und die Auswertungs-Engine erstmalig
          unter Live-Last steht, sind in der Saison 2026 Anfechtungen <strong>der eigenen
          Wertungsentscheidung</strong> (z. B. fehlerhafte Klassen- oder Geländezuordnung
          durch die Engine, Engine-Fehlinterpretation eines ENL-Signals, falsche Erkennung
          eines Motor-Einsatzes durch das Detektor-Labor nach § 11){' '}
          <strong>grundsätzlich kostenfrei</strong>. Engine-Fehler sollen nicht zu Lasten
          der Pilotinnen und Piloten gehen — die Beanstandung des eigenen Flugs ist Teil
          der Reifephase der Plattform und fließt direkt in die Detektor-Matrix ein.
        </p>
        <p>
          Für Proteste gegen die Wertung <em>anderer</em> Piloten (Mitbewerber-Verstoß,
          Manipulation, falsche Klassendeklaration) bleibt die Note auch in 2026 in voller
          Höhe von 250 € fällig. Ab Saison 2027 wird die Protestnote für Fremd-Proteste in
          allen Fällen einheitlich erhoben — eigene Wertungsanfechtungen bleiben jedoch
          dauerhaft kostenfrei, solange sie sachlich und nachvollziehbar begründet sind.
        </p>
        <p>
          <strong>Wirksamkeit:</strong> Ein Protest gilt erst dann als wirksam eingelegt, wenn
          das vollständig ausgefüllte Protestformular über die Plattform eingereicht wurde — bei
          Fremd-Protesten zusätzlich der Eingang der Protestnote vor Ablauf der 7-Tage-Frist
          beim Veranstalter vorliegt. Eigene Wertungsanfechtungen (siehe Übergangs­regelung
          oben) sind ohne Protestnote sofort wirksam, sobald das Formular eingereicht ist.
        </p>
        <p>
          <strong>Rückerstattung der Protestnote (sportrechtlicher Standard):</strong>
        </p>
        <AwardPflichtList items={[
          'Wird dem Protest stattgegeben, wird die Protestnote vollständig zurückerstattet.',
          'Wird dem Protest teilweise stattgegeben, erfolgt eine anteilige Rückerstattung nach billigem Ermessen der Rennleitung; die Quote orientiert sich am Erfolgsanteil und ist im Bescheid zu begründen.',
          'Wird der Protest zurückgewiesen, verbleibt die Protestnote beim Veranstalter zur Deckung des Prüfaufwands.',
          'Wird der Protest verspätet eingereicht oder formell unzulässig (fehlende Begründung, fehlender Eingang der Note), wird er ohne sachliche Prüfung zurückgewiesen; die Protestnote verbleibt beim Veranstalter.',
        ]} />
        <p>
          <strong>Nachweis eines geringeren Aufwands (§ 309 Nr. 5 lit. b BGB analog):</strong>{' '}
          Der Pilot ist im Fall einer Zurückweisung berechtigt, nachzuweisen, dass der tatsächliche
          Verwaltungsaufwand des Veranstalters deutlich geringer war als die pauschale Protestnote;
          in diesem Fall reduziert sich die einbehaltene Note auf den nachgewiesenen tatsächlichen
          Aufwand.
        </p>
        <p>
          <strong>Rechtsweg:</strong> Die Protestentscheidung wird durch die Jury der FREE GLIDE
          LEAGUE bzw. — sofern keine Jury benannt ist — durch den Administrator des Veranstalters
          getroffen. Sie ist eine sportliche Entscheidung im Sinne eines Preisausschreibens
          (§ 661 BGB); eine gerichtliche Überprüfung der Entscheidung und der Höhe der einbehaltenen
          Protestnote bleibt der Pilotin / dem Piloten unbenommen (§ 22).
        </p>
      </>
    ),
  },
  {
    num: '§ 16', numDisplay: '16', title: 'Pokale, Auszeichnungen, Preise', titleI18nKey: 'legal.wettbewerbTitles.t16',
    body: (
      <>
        <p>
          Nach Abschluss der Saison, Ablauf der Einreichungsfrist und der Protestfrist vergibt der
          Veranstalter die für die jeweilige Saison ausgeschriebenen{' '}
          <strong>Badges</strong>, <strong>Pokale</strong> sowie eine{' '}
          <strong>Teilnahmebescheinigung mit Leistungsdaten</strong> — letztere automatisch im
          persönlichen Logbuch der Pilotin / des Piloten als PDF abrufbar.
        </p>
        <p>
          Die Rennleitung stiftet privat insgesamt <strong>2 × 10 personalisierte Pokale</strong>,
          die an die Erstplatzierten der unterschiedlichen Bewertungsbereiche vergeben werden.
          Gewürdigt werden:
        </p>
        <AwardPflichtList items={[
          'Die Klassenwertungen (Club, Standard, Offen)',
          'Die Zeitwertungen (bis 30 min, bis 1 h, bis 2 h, bis 3 h, über 3 h)',
          'Die beste Streckenflugleistung',
          'Die besten Jahresendergebnisse, getrennt nach Hangflug und Flachland',
        ]} />
        <p>
          Ein Anspruch auf Barauszahlung, Umtausch oder Übertragung von Pokalen besteht nicht.
          Die Pokale sind privat gestiftet und stellen keine Sachpreise im handelsrechtlichen Sinne dar.
        </p>
        <p>
          Auf Preisausschreiben und ähnliche Gewinnspiele besteht nach § 312g Abs. 2 Nr. 12 BGB
          kein gesetzliches Widerrufsrecht.
        </p>
        <p>
          Die Preisverleihung findet üblicherweise in der dritten Januarwoche des Folgejahres statt.
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // VII. WAHRHEIT & PRÜFUNG
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 17', numDisplay: '17', title: 'Wahrheitspflicht & Prüfungsbefugnis', titleI18nKey: 'legal.wettbewerbTitles.t17',
    body: (
      <>
        <p>
          Mit der Einreichung eines Fluges versichert die Teilnehmerin / der Teilnehmer, dass
          alle Angaben vollständig und wahrheitsgemäß sind und der eingereichte Datensatz dem
          tatsächlich geflogenen Flug entspricht. Insbesondere gilt der hinterlegte{' '}
          <strong>Klarname</strong> als <strong>Basis der Teilnahmeberechtigung</strong> —
          dieser muss dem im amtlichen Personalausweis bzw. Reisepass eingetragenen Namen der
          Pilotin / des Piloten entsprechen. Die Verwendung eines anderen Klarnamens als des
          eigenen führt zur Disqualifikation und zum Ausschluss von der weiteren Teilnahme
          (§ 14d der Datenschutzerklärung).
        </p>
        <p>
          Der Veranstalter ist berechtigt, Flüge manuell, automatisiert oder teilautomatisiert zu
          prüfen. Er darf insbesondere
        </p>
        <AwardPflichtList items={[
          'Plausibilitätsprüfungen durchführen,',
          'Flüge umklassifizieren,',
          'Datensätze zurückweisen,',
          'Flüge vorläufig oder endgültig sperren,',
          'zusätzliche Nachweise anfordern.',
        ]} />
      </>
    ),
  },
  {
    num: '§ 18', numDisplay: '18', title: 'Ausschlussgründe', titleI18nKey: 'legal.wettbewerbTitles.t18',
    body: (
      <>
        <p>Von der Wertung ausgeschlossen werden können insbesondere:</p>
        <AwardPflichtList items={[
          'Verspätet eingereichte Flüge,',
          'Unvollständige oder technisch nicht auswertbare Datensätze,',
          'Falsche Modell- oder Klassenangaben,',
          'Verwendung eines anderen Klarnamens als des im amtlichen Personalausweis bzw. Reisepass der Pilotin / des Piloten eingetragenen Namens (siehe § 17 sowie § 14d Datenschutzerklärung),',
          'Fälschung oder Manipulation von Logger-Daten, einschließlich des Motor-Ausschluss-Signals (ENL/MOP),',
          'Einsatz nicht zugelassener Logger oder manipulierter Hardware,',
          'Verwendung von Doppel- oder Fake-Accounts,',
          'Nachgewiesene Manipulation von Zeit-, Höhen- oder Positionsdaten in der IGC-Datei,',
          'Multistart-Versuche entgegen § 11,',
          'Überschreitung der maximalen Wertungshöhe von 750 m AGL,',
          'Verstöße gegen geltendes Recht am Flugort, sofern diese durch einen wirksamen Protest auf der Plattform bekannt gemacht und glaubhaft begründet werden,',
          'Verstöße gegen die Verhaltensregeln gemäß § 19,',
          'Wiederholte oder schwerwiegende Regelverstöße — in diesen Fällen kann der Veranstalter die Pilotin / den Piloten dauerhaft von der Serie ausschließen.',
        ]} />
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // VIII. VERHALTENSREGELN & SANKTIONEN
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 19', numDisplay: '19', title: 'Verhaltensregeln & Sanktionen', titleI18nKey: 'legal.wettbewerbTitles.t19',
    body: (
      <>
        <p>
          <strong>(1) Verhaltensregeln.</strong> Teilnehmerinnen und Teilnehmer der Serie verpflichten
          sich zu respektvollem, ehrlichem und sportlichem Verhalten in der internen
          Plattform-Kommunikation (Chat, Kommentare, Direktnachrichten, Bug-Reports) sowie in jeder
          Kommunikation per E-Mail oder anderen Kanälen, die mit der FREE GLIDE LEAGUE in
          Zusammenhang steht.
        </p>
        <p>
          <strong>(2) Sanktionsstufen.</strong> Bei Verstößen gegen diese Verhaltensregeln,
          insbesondere bei Beleidigung, Diskriminierung, Belästigung, Mobbing, Drohungen,
          vorsätzlicher Falschdarstellung anderer Teilnehmer oder Manipulationsversuchen, ist der
          Veranstalter berechtigt, gestaffelt zu sanktionieren:
        </p>
        <AwardPflichtList items={[
          'Schriftliche Verwarnung (Abmahnung) per Plattform-Nachricht oder E-Mail,',
          'Temporäre Sperre des Accounts (7, 30 oder 90 Tage),',
          'Dauerhafte Sperre des Accounts und Ausschluss aus der Serie,',
          'Komplette Account-Löschung einschließlich aller bisher eingereichten Flüge und Wertungen, soweit DSGVO-rechtlich zulässig.',
        ]} />
        <p>
          Der Veranstalter wählt die angemessene Stufe nach Art und Schwere des Verstoßes. Vor
          temporären oder dauerhaften Sperren ergeht in der Regel eine schriftliche Verwarnung mit
          Frist zur Stellungnahme.
        </p>
        <p>
          <strong>(3) Sofortige Sperre ohne Abmahnung.</strong> Bei schwerwiegenden Verstößen, die
          geeignet sind, die Gemeinschaft zu gefährden, in Verruf zu bringen oder rechtlichen Schaden
          anzurichten, kann die Sperre oder Löschung sofort und ohne vorherige Abmahnung erfolgen.
          Solche Verstöße sind insbesondere:
        </p>
        <AwardPflichtList items={[
          'Vorsätzliche Manipulation von Logger-Daten oder Wertungs-Eingaben,',
          'Beleidigung, Hassrede, Diskriminierung, Drohungen, Belästigung oder Mobbing in der Plattform-Kommunikation oder per E-Mail im FGL-Kontext,',
          'Veröffentlichung personenbezogener Daten anderer Pilotinnen / Piloten ohne deren Einwilligung,',
          'Aufruf zu illegalen Handlungen oder Verstöße gegen Strafgesetze im Zusammenhang mit der Plattform,',
          'Wiederholte schwere Verstöße trotz vorausgegangener Verwarnung,',
          'Verhalten am Flugort, das die FREE GLIDE LEAGUE öffentlich in Verruf bringt.',
        ]} />
        <p>
          <strong>(4) Verfahren.</strong> Sanktionsentscheidungen werden der oder dem Betroffenen
          schriftlich mitgeteilt. Eine sofortige Sperre kann ohne vorherige Begründung erfolgen;
          die schriftliche Begründung wird unverzüglich nachgereicht. Eine gerichtliche Überprüfung
          der Entscheidung bleibt der oder dem Betroffenen unbenommen.
        </p>
        <p>
          <strong>(5) AGG-Schutzklausel.</strong> Sanktionsentscheidungen erfolgen ausschließlich
          aufgrund nachprüfbarer Verstöße gegen diese Wettbewerbsordnung oder gegen geltendes Recht.
          Eine Diskriminierung im Sinne des AGG findet nicht statt.
        </p>
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // IX. SCHLUSSVORSCHRIFTEN
  // ─────────────────────────────────────────────────────────────
  {
    num: '§ 20', numDisplay: '20', title: 'Veröffentlichung, Datenschutz & Bewegungsdaten-Hinweis', titleI18nKey: 'legal.wettbewerbTitles.t20',
    body: (
      <>
        <p>
          Der Veranstalter ist berechtigt, Name (oder Pilotenpseudonym), Region/Fluggebiet,
          Modellangaben, Distanz, Flugdauer, Wertungspunkte, Klassenzuordnung und damit verbundene
          Wertungsergebnisse im Rahmen der Serie öffentlich auf der Plattform zu veröffentlichen,
          soweit dies für die Durchführung und Darstellung der Serie erforderlich und
          datenschutzrechtlich zulässig ist.
        </p>
        <p>
          Mit der Einreichung eines Fluges willigt die Pilotin / der Pilot ausdrücklich in die
          Veröffentlichung dieser Daten zum Zweck der Durchführung der Wertungsserie ein
          (Art. 6 Abs. 1 lit. a und b DSGVO).
        </p>
        <p>
          <strong style={{ color: 'var(--award-gold)' }}>Hinweis auf Bewegungsdaten:</strong>{' '}
          Mit dieser Einwilligung erkennt die Pilotin / der Pilot ausdrücklich an, dass aus den im
          Zusammenhang mit Flügen veröffentlichten Daten — insbesondere Datum, Uhrzeit, Flugort,
          Flugdauer und Häufigkeit von Flügen — unter Umständen <strong>Rückschlüsse auf
          Aufenthaltsorte und Bewegungsmuster</strong> gezogen oder interpretiert werden können.
          Die Pilotin / der Pilot stimmt dieser Möglichkeit ausdrücklich zu.
        </p>
        <p>
          Die Einwilligung kann jederzeit mit Wirkung für die Zukunft widerrufen werden
          (Art. 7 Abs. 3 DSGVO). Der Widerruf führt zum Ausschluss aus laufenden Wertungen und zur
          Entfernung der betroffenen Veröffentlichungen. Die Rechtmäßigkeit der aufgrund der
          Einwilligung bis zum Widerruf erfolgten Verarbeitung bleibt unberührt. Einzelheiten regelt
          die <Link href="/datenschutz" className="award-link">Datenschutzerklärung</Link>.
        </p>
      </>
    ),
  },
  {
    num: '§ 21', numDisplay: '21', title: 'Änderungen der Wettbewerbsordnung', titleI18nKey: 'legal.wettbewerbTitles.t21',
    body: (
      <>
        <p>
          Der Veranstalter darf die Wettbewerbsordnung mit Wirkung für die Zukunft anpassen, wenn
          hierfür ein sachlicher Grund besteht — insbesondere zur Schließung von Regelungslücken,
          zur technischen Weiterentwicklung, zur Missbrauchsvermeidung oder zur Anpassung an
          geänderte Rechtslagen.
        </p>
        <p>
          <strong>Änderungsmechanismus (BGH-konform, vgl. BGH XI ZR 26/20):</strong> Wesentliche
          Änderungen werden den Teilnehmerinnen und Teilnehmern mindestens{' '}
          <strong>sechs Wochen vor Inkrafttreten</strong> auf der Plattform und per E-Mail
          mitgeteilt. Wird der Änderung innerhalb dieser Frist nicht widersprochen, gelten die
          Änderungen als angenommen. Bei Widerspruch endet die Teilnahme an der Serie zum Zeitpunkt
          des Inkrafttretens der Änderung; bereits eingereichte Flüge bleiben gewertet.
        </p>
        <p>
          Bereits abgeschlossene Wertungen sollen nur aus wichtigem sachlichem Grund nachträglich
          geändert werden. Bei Änderungen während einer laufenden Saison wird der Veranstalter auf
          Auswirkungen für bereits eingereichte Flüge ausdrücklich hinweisen.
        </p>
      </>
    ),
  },
  {
    num: '§ 22', numDisplay: '22', title: 'Anwendbares Recht & Gerichtsstand', titleI18nKey: 'legal.wettbewerbTitles.t22',
    body: (
      <>
        <p>
          Die FGL-Serie wird nach deutschem Recht und unter Beachtung der DSGVO betrieben. Für
          Streitigkeiten aus oder im Zusammenhang mit dieser Wettbewerbsordnung gilt deutsches Recht
          unter Ausschluss des UN-Kaufrechts (CISG).
        </p>
        <p>
          Gerichtsstand für Streitigkeiten ist — soweit gesetzlich zulässig — der Sitz des
          Veranstalters in <strong>Berlin, Deutschland</strong>.
        </p>
        <p>
          Gegenüber Verbrauchern im Sinne des § 13 BGB bleibt der zwingende Verbraucher- und
          Wohnsitz-Schutz nach Art. 6 Rom-I-VO und Art. 17 ff. EuGVVO unberührt; diese können den
          Veranstalter auch an ihrem Wohnsitzgericht verklagen und sich auf das zwingende Recht
          ihres Aufenthaltsstaates berufen.
        </p>
      </>
    ),
  },
]

export default function WettbewerbsordnungPage() {
  const { t } = useTranslation()
  return (
    <AwardLegalShell
      eyebrow={t('legal.wettbewerbsordnung.eyebrow')}
      meta={t('legal.wettbewerbsordnung.metaSeason', { season: SEASON })}
      title={t('legal.wettbewerbsordnung.title')}
      gradWord={t('legal.wettbewerbsordnung.gradWord')}
      lead={t('legal.wettbewerbsordnung.lead')}
      chips={[
        { label: t('legal.wettbewerbsordnung.chip1') },
        { label: t('legal.wettbewerbsordnung.chip2') },
        { label: t('legal.wettbewerbsordnung.chip3', { season: String(SEASON) }) },
        { label: t('legal.wettbewerbsordnung.chip4') },
      ]}
      notice={t('legal.wettbewerbsordnung.notice')}
      tocEyebrow={t('legal.wettbewerbsordnung.tocEyebrow')}
      tocTitle={t('legal.shared.tocTitle')}
      articles={articles}
      closingEyebrow={t('legal.wettbewerbsordnung.closingEyebrow')}
      closingTitle={
        <>
          {t('legal.wettbewerbsordnung.closingPrefix')}{' '}
          <Link href="/nutzungsbedingungen" className="award-link">{t('legal.wettbewerbsordnung.closingNutzungsbedingungen')}</Link>{' '}
          {t('legal.wettbewerbsordnung.closingAnd')}{' '}
          <Link href="/datenschutz" className="award-link">{t('legal.wettbewerbsordnung.closingDatenschutz')}</Link>.
        </>
      }
      primaryCta={{ href: '/wertung', label: t('legal.wettbewerbsordnung.primaryCta') }}
      secondaryCta={{ href: '/', label: t('legal.wettbewerbsordnung.secondaryCta') }}
    />
  )
}
